import NameCellWithImage, { ChipCell } from 'components/controls/image-cell';
import { useWorkspace } from 'hooks/useWorkspace';
import { MRT_ColumnDef } from 'material-react-table';
import { PROMOTION_TYPE_COLOR } from './type';
import { LIFECYCLE_STATUS_COLOR } from './lifecycle-status';
import { useTranslation } from 'react-i18next';
import {
    Home as HomePage,
    RestaurantMenu as MenuPage,
    ShoppingCartCheckout as CheckoutPage,
} from '@mui/icons-material';
import { PAGE_TYPE_COLOR } from './page-type';
import { useMemo } from 'react';
import { ToolTip } from 'components/controls';

export type PromotionColumns = {
    id: string;
    name: string;
    type: string;
    placeId: string;
};

export const usePromotionTableColumns = (): MRT_ColumnDef<PromotionColumns>[] => {
    const { t } = useTranslation();
    const { baseWorkspaceUrl } = useWorkspace();

    const PAGE_ICONS = useMemo(
        () => ({
            HOME: <HomePage />,
            CHECKOUT: <CheckoutPage />,
            MENU: <MenuPage />,
        }),
        [],
    );

    return useMemo(
        () => [
            {
                accessorKey: 'name',
                header: t('TABLE_HEADERS.NAME'),
                enableColumnActions: false,
                Cell: ({ cell }) => {
                    const path = `${baseWorkspaceUrl}/promotion/${cell.row.original.placeId}/edit/${cell.row.original.id}`;
                    return (
                        <ToolTip
                            disableHoverListener={cell.row.original['name'].length < 25}
                            title={cell.getValue<string>()}
                            placement="top-end"
                        >
                            <NameCellWithImage cell={cell} isLink path={path} target="_self" showImage={false} />
                        </ToolTip>
                    );
                },
                size: 200,
            },
            {
                accessorKey: 'type',
                header: t('TABLE_HEADERS.TYPE'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 150,
                Cell: ({ cell }) => (
                    <ChipCell
                        value={cell.getValue<string>()}
                        statusColorMap={PROMOTION_TYPE_COLOR}
                        translationKey="TYPES"
                    />
                ),
            },
            {
                accessorKey: 'page',
                header: t('TABLE_HEADERS.PAGE'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 150,
                Cell: ({ cell }) => (
                    <ChipCell
                        value={cell.getValue<string>().toUpperCase()}
                        statusColorMap={PAGE_TYPE_COLOR}
                        icon={PAGE_ICONS[cell.getValue<string>().toUpperCase()] || null}
                        translationKey="TYPES"
                    />
                ),
            },
            {
                accessorKey: 'lifecycleStatus',
                header: t('TABLE_HEADERS.STATUS'),
                enableColumnActions: false,
                enableColumnFilter: true,
                filterVariant: 'autocomplete',
                size: 150,
                Cell: ({ cell }) => (
                    <ChipCell
                        value={cell.getValue<string>()}
                        statusColorMap={LIFECYCLE_STATUS_COLOR}
                        translationKey="STATUS"
                    />
                ),
            },
            {
                accessorKey: 'startDate',
                header: t('TABLE_HEADERS.START_DATE'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 100,
            },
            {
                accessorKey: 'endDate',
                header: t('TABLE_HEADERS.END_DATE'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 100,
            },
            {
                accessorKey: 'createdAt',
                header: t('TABLE_HEADERS.CREATION_DATE'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 100,
            },
        ],
        [t, baseWorkspaceUrl, PAGE_ICONS],
    );
};
